import React, { useEffect, useState } from "react";
import LinesEllipsis from "react-lines-ellipsis";
import classNames from "classnames";
import { VenuesClient, VenueModel, BookingMethod, VenueHeadquarterDataViewModel, RatingClient, IAverageRatingModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { Modal, ModalBody, ModalHeader } from "Components/ReactstrapSlim";
import LsLoading from "Components/LsLoading";
import { RatingBadge } from "Components/Rating";
import * as Icons from "Components/Icons";
import { HealthAndSafetyFullBadge } from "Components/HealthAndSafetyBadge";
import { ImageSwiper } from "Components/ImageSwiper";
import { InstantBookBadge } from "Components/InstantBookBadge";
import { DesignContextProvider } from "Components/DesignContext";
import ReservationCalendar from "./ReservationCalendar";
import VenueCustomCta from "./VenueCustomCta";
import classes from "./styles.module.scss";

interface VenueCalendarModalProps {
   isOpen: boolean,
   venueId: Guid,
   /** yyyy-MM-DD */
   selectedDate?: string,
   container?: HTMLElement | string,
   toggle: () => void,
   refreshUpcomingReservationList?: () => void
}

export const VenueCalendarModal: React.FC<VenueCalendarModalProps> = ({ isOpen, venueId, selectedDate, container, toggle, refreshUpcomingReservationList }) => {
   const venuesClient = useApiClient(VenuesClient);
   const ratingClient = useApiClient(RatingClient);
   const [[venue, headquarterData, healthAndSafetyPolicy, rating], setVenueData]
      = useState<[
         VenueModel | undefined,
         VenueHeadquarterDataViewModel | undefined,
         string | undefined,
         IAverageRatingModel | undefined]>
         ([undefined, undefined, undefined, undefined]);
   const [clampedDescription, _setClampedDescription] = useState<boolean>(true);

   const [isHidden, setIsHidden] = useState<boolean>(false);

   const loadVenueCalendarModel = async (abortSignal?: AbortSignal) => {
      try {
         const data = await Promise.all([
            venuesClient.get(venueId),
            venuesClient.getHeadquarterData(venueId).catch(_ => undefined),
            venuesClient.healthAndSafetyPolicy(venueId).catch(_ => undefined),
            ratingClient.average(venueId, null)
         ]);
         setVenueData(data);
      }
      catch {
      }
   }

   useEffect(() => {
      const abortController = new AbortController();
      loadVenueCalendarModel(abortController.signal);
      return function cancel() {
         abortController.abort();
      }
   }, [venueId]);

   return (<Modal isOpen={isOpen} toggle={toggle} className={classNames(classes.modal, { [classes.modalHidden]: isHidden })} centered size="lg" container={container}>
      <DesignContextProvider name="primary" >
         {!venue
            ? <LsLoading isLoading={true} />
            : <>
               <ModalHeader toggle={toggle} className="h7">
                  {
                     headquarterData?.customCTA
                        ? "Create Reservation"
                        : "Choose a space and time"
                  }
               </ModalHeader>
               <ModalBody>
                  <div className={classes.venue}>
                     <div className={classes.gallery}>
                        <ImageSwiper imageUrls={venue.imageUrls || []} />
                     </div>
                     <div className={classes.info}>
                        <div className={classes.name}>{venue.name}</div>
                        <div className={classes.address}>{venue.address}</div>
                        <div className={classes.badges}>
                           {healthAndSafetyPolicy ? <HealthAndSafetyFullBadge /> : null}
                           {venue.defaultBookingMethodHourly == BookingMethod.BookItNow && <InstantBookBadge />}
                           <RatingBadge averageRating={rating?.average} ratingCount={rating?.count} theme="yellow" />
                        </div>
                        <div className={classes.description}>
                           {clampedDescription
                              ? <LinesEllipsis text={venue.description || ""} maxLine="3" ellipsis={<span>... <a href={venue.url} target="_blank">Learn more</a></span>} trimRight />
                              : venue.description
                           }

                        </div>
                     </div>
                     <div className={classes.map}>
                        <img src={venue.venueMapImageUrl} alt="venue map location" />
                        <Icons.Pin className={classes.mapPin} />
                     </div>
                  </div>
                  {
                     headquarterData?.customCTA
                        ? <VenueCustomCta headquarterData={headquarterData} />
                        : <ReservationCalendar
                           venue={venue}
                           refreshUpcomingReservationList={refreshUpcomingReservationList}
                           selectedDate={selectedDate}
                           container={container}
                           toggle={toggle}
                           setIsHidden={setIsHidden}
                        />
                  }
               </ModalBody>
            </>}
      </DesignContextProvider>
   </Modal>)
}